import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import CandidateInterviewListFilterOptions from './CandidateInterviewListFilterOptions'
import CandidateInterviewListTable from './CandidateInterviewListTable'
import { reduxServices } from '../../../../reducers/reduxServices'
import { useAppDispatch, useTypedSelector } from '../../../../stateStore'
import OCard from '../../../../components/ReusableComponent/OCard'
import OLoadingSpinner from '../../../../components/ReusableComponent/OLoadingSpinner'
import { ApiLoadingState } from '../../../../middleware/api/apiList'
import { LoadingType } from '../../../../types/Components/loadingScreenTypes'
import usePermissionToView, {
  PermissionEnum,
} from '../../../../middleware/hooks/usePermissionToView'
import {
  InterviewCandidateListConfigType,
  GetInterviewCandidateListType,
} from '../../../../types/Recruitment/CandidateList/CandidateInterviewListTypes'
import {
  PaginationOptionType,
  usePaginationRevised,
} from '../../../../middleware/hooks/usePaginationRevised'
import {
  customPageSize,
  initialCandidateInterviewFilterValues,
  initialPage,
} from '../CandidateListHelpers'
import { asyncApiRetryHelper } from '../../../../utils/asyncApiRetryUtils'
import usePaginationParent from '../../../../middleware/hooks/usePaginationParent'
import useRouteHistory from '../../../../middleware/hooks/useRouteHistory'

//* Make sure to list all the expected origin routes that will be visited by the user within the module
const expectedOrigin = [
  '/candidateInterviewList',
  '/jobschedulecandidateList',
  '/addNewCandidate',
  '/editcandidate',
  '/candidatetimeline',
  '/jobinfo',
  'scheduleInterview',
]

// This is the custom hook that will be used to handle the route logic of the component
export function CandidateInterviewListRouteLogic(): void {
  const { route } = useRouteHistory()
  const dispatch = useAppDispatch()

  const getFilterData = useTypedSelector(
    reduxServices.candidateInterviewList.selectors
      .getInterviewCandidateListBody,
  )

  useEffect(() => {
    if (expectedOrigin.includes(`/${route}`)) {
      //* Retain the filter data in redux store when the route is from a realted component
      dispatch(
        reduxServices.candidateInterviewList.actions.setInterviewCandidateListBody(
          getFilterData,
        ),
      )
    } else {
      //* Reset the filter data in redux store when the route is from a another component
      dispatch(
        reduxServices.candidateInterviewList.actions.setInterviewCandidateListBody(
          initialCandidateInterviewFilterValues,
        ),
      )
    }
  }, [route])
}

const CandidateInterviewList = (): JSX.Element => {
  usePermissionToView('Candidate List', PermissionEnum.View)
  const history = useHistory()
  const dispatch = useAppDispatch()

  const isLoading = useTypedSelector(
    reduxServices.candidateInterviewList.selectors.getLoading,
  )
  const listSize = useTypedSelector(
    reduxServices.candidateInterviewList.selectors
      .getInterviewCandidateListSize,
  )

  //* To fix global pagination issue of filters you need to use the redux store to store the filter data in one object
  //* do not separate each filter as selector it is very tiring to maintain and fix individual states
  const getFilterData = useTypedSelector(
    reduxServices.candidateInterviewList.selectors
      .getInterviewCandidateListBody,
  )

  //* make a local state for the filter data as it will be used to display filter data in UI only
  //* and it will not update the redux store yet
  const [filterBody, setFilterBody] =
    useState<GetInterviewCandidateListType>(getFilterData)

  //* When implementing the new pagination (revised), always make a function that calls the main api for the table data
  const reloadData = async (
    size: number,
    page: number,
    config?: InterviewCandidateListConfigType,
  ) => {
    let tempConfig = {} as InterviewCandidateListConfigType
    if (config) tempConfig = config

    const payload: GetInterviewCandidateListType = {
      ...getFilterData, // spread the filter data from redux store
      ...tempConfig, // spread any custom filter data need to be set based on unique cases
      startIndex: size * (page - 1),
      endIndex: size * page,
    }

    //* this fetch data function is a must to be async and return a boolean in order to be retried when it fails
    const fetchData = async (): Promise<boolean> => {
      try {
        const ret = await dispatch(
          reduxServices.candidateInterviewList.getInterviewCandidatesList(
            payload,
          ),
        )
        if (ret?.payload === 403) {
          history.push('/forbidden')
          return false
        }

        const retStatus = ret.meta.requestStatus === 'fulfilled'
        //* So this is when we update the filter data in redux store, when the user clicks the view button or pagination
        if (retStatus)
          dispatch(
            reduxServices.candidateInterviewList.actions.setInterviewCandidateListBody(
              payload,
            ),
          )
        return retStatus
      } catch (error) {
        console.error('Error fetching data:', error)
        return false
      }
    }
    await asyncApiRetryHelper(fetchData)
  }

  //* This is called when the route is from another module that is not related to the current component
  const resetCallback = () => {
    reloadData(customPageSize, initialPage, getFilterData)
    resetPagination('reset candidateInterviewList')
  }

  const option: PaginationOptionType = {
    totalItemCount: listSize,
    customPageSize,
    expectedOrigin,
    initialPage,
    callback: resetCallback,
  }

  const { storedPageCache, resetPagination, resetPaginationAt } =
    usePaginationRevised(option)

  //* This is called when the route is related to the current component
  const manualCallback = () => {
    // Since this is component is only a child or related to a parent component,
    // this the only function need to be used to retain filter and data
    reloadData(customPageSize, initialPage, getFilterData)
  }

  usePaginationParent(
    '/candidateInterviewList', //* parent route will always be the current route of the component
    '', //* related route is only for any buttons in the component that will redirect to other route; except for the back button
    resetPaginationAt,
    manualCallback,
  )

  return (
    <>
      <OCard
        className="mb-4 myprofile-wrapper"
        title="Candidate Interview List"
        CBodyClassName="ps-0 pe-0"
        CFooterClassName="d-none"
      >
        <CandidateInterviewListFilterOptions
          filterBody={filterBody}
          setFilterBody={setFilterBody}
          reloadData={reloadData}
          resetPagination={resetPagination}
        />
        {isLoading === ApiLoadingState.succeeded ? (
          <CandidateInterviewListTable
            currentPage={storedPageCache.page}
            pageSize={storedPageCache.size}
            reloadData={reloadData}
            resetPagination={resetPagination}
          />
        ) : (
          <OLoadingSpinner type={LoadingType.PAGE} />
        )}
      </OCard>
    </>
  )
}

export default CandidateInterviewList
